import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ReportSourcesView.css'; // Reuse existing CSS
import '@fortawesome/fontawesome-free/css/all.min.css';
import Loader from './components/Loader';
import PageInfo from './components/PageInfo';
import env from './utils/env';

// Define the interface for ReportSourceCandidate
export interface ReportSourceCandidate {
    _id: string;
    url: string;
    is_pdf?: boolean;
    links_processed?: Date | boolean;
    insert_timestamp?: string;
    update_timestamp?: string;
    source_search_result_id?: string;
    source_url?: string;
    link_count?: number;
    crawler_engine?: string;
    checking_failed?: string;
    processors_to_run?: string[];
    // Discovery/provenance fields
    hop_count?: number;
    discovery_chain?: string[];
    root_search_result_id?: string;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    candidates: ReportSourceCandidate[];
    pagination?: PaginationMetadata;
}

interface FilterOptions {
    crawlerEngines: string[];
}

interface Stats {
    total: number;
    pdfCount: number;
    nonPdfCount: number;
    withErrors: number;
    processedCount: number;
    byCrawlerEngine: Array<{ engine: string; count: number }>;
}

const ReportSourceCandidatesView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const [searchParams, setSearchParams] = useSearchParams();

    // State
    const [candidates, setCandidates] = useState<ReportSourceCandidate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
    const [stats, setStats] = useState<Stats | null>(null);

    // URL-based state
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const searchQuery = searchParams.get('search') || '';
    const filterIsPdf = searchParams.get('is_pdf') || '';
    const filterCrawlerEngine = searchParams.get('crawler_engine') || '';
    const filterHasError = searchParams.get('has_error') || '';
    const limit = 50;

    // Update URL params helper
    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Fetch candidates
    const fetchCandidates = useCallback(async () => {
        if (!authLoaded || !token) return;

        setLoading(true);

        const params = new URLSearchParams();
        params.set('page', String(currentPage));
        params.set('limit', String(limit));
        if (searchQuery) params.set('search', searchQuery);
        if (filterIsPdf) params.set('is_pdf', filterIsPdf);
        if (filterCrawlerEngine) params.set('crawler_engine', filterCrawlerEngine);
        if (filterHasError) params.set('has_error', filterHasError);

        try {
            const response = await axios.get<ApiResponse>(
                `${apiUrl}/report-source-candidates?${params.toString()}`,
                {
                    headers: {
                        'x-api-key': apiKey,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            setCandidates(response.data.candidates || []);
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching report source candidates:', error);
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    }, [authLoaded, token, apiUrl, apiKey, currentPage, searchQuery, filterIsPdf, filterCrawlerEngine, filterHasError]);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        if (!authLoaded || !token) return;

        try {
            const response = await axios.get<FilterOptions>(
                `${apiUrl}/report-source-candidates/filter-options`,
                {
                    headers: {
                        'x-api-key': apiKey,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    }, [authLoaded, token, apiUrl, apiKey]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        if (!authLoaded || !token) return;

        try {
            const response = await axios.get<Stats>(
                `${apiUrl}/report-source-candidates/stats`,
                {
                    headers: {
                        'x-api-key': apiKey,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [authLoaded, token, apiUrl, apiKey]);

    // Initial load
    useEffect(() => {
        if (authLoaded && token) {
            fetchFilterOptions();
            fetchStats();
        }
    }, [authLoaded, token, fetchFilterOptions, fetchStats]);

    // Fetch candidates when filters change
    useEffect(() => {
        fetchCandidates();
    }, [fetchCandidates]);

    // Format date
    const formatDate = (dateString: string | undefined | null) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return '-';
        }
    };

    // Truncate URL for display
    const truncateUrl = (url: string, maxLength: number = 60) => {
        if (url.length <= maxLength) return url;
        return url.substring(0, maxLength) + '...';
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        updateSearchParams({ page: String(newPage) });
    };

    // Handle search
    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const query = formData.get('search') as string;
        updateSearchParams({ search: query || null, page: '1' });
    };

    // Clear filters
    const clearFilters = () => {
        setSearchParams(new URLSearchParams());
    };

    // Check if any filters are active
    const hasActiveFilters = searchQuery || filterIsPdf || filterCrawlerEngine || filterHasError;

    if (!authLoaded) {
        return <Loader text={t('loading.authentication')} />;
    }

    return (
        <div className="report-sources-container">
            <div className="report-sources-header">
                <h1>{t('reportSourceCandidates.title')}</h1>
                {stats && (
                    <div className="stats-summary">
                        <span className="stat-item">
                            <i className="fas fa-database"></i> {stats.total.toLocaleString()} {t('reportSourceCandidates.total')}
                        </span>
                        <span className="stat-item">
                            <i className="fas fa-file-pdf"></i> {stats.pdfCount.toLocaleString()} {t('reportSourceCandidates.pdfs')}
                        </span>
                        <span className="stat-item">
                            <i className="fas fa-check-circle"></i> {stats.processedCount.toLocaleString()} {t('reportSourceCandidates.processed')}
                        </span>
                        <span className="stat-item error">
                            <i className="fas fa-exclamation-triangle"></i> {stats.withErrors.toLocaleString()} {t('reportSourceCandidates.errors')}
                        </span>
                    </div>
                )}
            </div>

            {/* Filter controls */}
            <div className="filter-controls">
                <form onSubmit={handleSearch} className="search-form">
                    <input
                        type="text"
                        name="search"
                        placeholder={t('reportSourceCandidates.searchPlaceholder')}
                        defaultValue={searchQuery}
                        className="search-input"
                    />
                    <button type="submit" className="search-button">
                        <i className="fas fa-search"></i>
                    </button>
                </form>

                <div className="filter-dropdowns">
                    <select
                        value={filterIsPdf}
                        onChange={(e) => updateSearchParams({ is_pdf: e.target.value || null, page: '1' })}
                        className="filter-select"
                    >
                        <option value="">{t('reportSourceCandidates.filters.allTypes')}</option>
                        <option value="true">{t('reportSourceCandidates.filters.pdfOnly')}</option>
                        <option value="false">{t('reportSourceCandidates.filters.nonPdfOnly')}</option>
                    </select>

                    {filterOptions && filterOptions.crawlerEngines.length > 0 && (
                        <select
                            value={filterCrawlerEngine}
                            onChange={(e) => updateSearchParams({ crawler_engine: e.target.value || null, page: '1' })}
                            className="filter-select"
                        >
                            <option value="">{t('reportSourceCandidates.filters.allEngines')}</option>
                            {filterOptions.crawlerEngines.map((engine) => (
                                <option key={engine} value={engine}>{engine}</option>
                            ))}
                        </select>
                    )}

                    <select
                        value={filterHasError}
                        onChange={(e) => updateSearchParams({ has_error: e.target.value || null, page: '1' })}
                        className="filter-select"
                    >
                        <option value="">{t('reportSourceCandidates.filters.allStatuses')}</option>
                        <option value="true">{t('reportSourceCandidates.filters.withErrors')}</option>
                        <option value="false">{t('reportSourceCandidates.filters.withoutErrors')}</option>
                    </select>

                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="clear-filters-button">
                            <i className="fas fa-times"></i> {t('reportSourceCandidates.filters.clear')}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <Loader text={t('loading.reportSourcesCandidates')} />
            ) : candidates.length === 0 ? (
                <div className="no-results">
                    <i className="fas fa-inbox"></i>
                    <p>{t('reportSourceCandidates.noResults')}</p>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="report-sources-table">
                            <thead>
                                <tr>
                                    <th>{t('reportSourceCandidates.headers.url')}</th>
                                    <th>{t('reportSourceCandidates.headers.type')}</th>
                                    <th>{t('reportSourceCandidates.headers.engine')}</th>
                                    <th>{t('reportSourceCandidates.headers.hops')}</th>
                                    <th>{t('reportSourceCandidates.headers.links')}</th>
                                    <th>{t('reportSourceCandidates.headers.status')}</th>
                                    <th>{t('reportSourceCandidates.headers.created')}</th>
                                    <th>{t('reportSourceCandidates.headers.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((candidate) => (
                                    <tr key={candidate._id} className={candidate.checking_failed ? 'has-error' : ''}>
                                        <td className="url-cell" title={candidate.url}>
                                            <a href={candidate.url} target="_blank" rel="noopener noreferrer">
                                                {truncateUrl(candidate.url)}
                                            </a>
                                        </td>
                                        <td>
                                            {candidate.is_pdf ? (
                                                <span className="badge badge-pdf">
                                                    <i className="fas fa-file-pdf"></i> PDF
                                                </span>
                                            ) : (
                                                <span className="badge badge-html">
                                                    <i className="fas fa-globe"></i> HTML
                                                </span>
                                            )}
                                        </td>
                                        <td>{candidate.crawler_engine || '-'}</td>
                                        <td>
                                            {candidate.hop_count !== undefined ? (
                                                <span className={`badge ${candidate.hop_count === 1 ? 'badge-success' : 'badge-info'}`} title={t('reportSourceCandidates.hopCountTooltip', { count: candidate.hop_count })}>
                                                    {candidate.hop_count}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>{candidate.link_count ?? '-'}</td>
                                        <td>
                                            {candidate.checking_failed ? (
                                                <span className="badge badge-error" title={candidate.checking_failed}>
                                                    <i className="fas fa-times-circle"></i> {t('reportSourceCandidates.status.failed')}
                                                </span>
                                            ) : candidate.links_processed ? (
                                                <span className="badge badge-success">
                                                    <i className="fas fa-check-circle"></i> {t('reportSourceCandidates.status.processed')}
                                                </span>
                                            ) : (
                                                <span className="badge badge-pending">
                                                    <i className="fas fa-clock"></i> {t('reportSourceCandidates.status.pending')}
                                                </span>
                                            )}
                                        </td>
                                        <td>{formatDate(candidate.insert_timestamp)}</td>
                                        <td>
                                            <button
                                                className="action-button"
                                                onClick={() => navigate(`/report-source-candidates/${candidate._id}`)}
                                                title={t('reportSourceCandidates.viewDetails')}
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && (
                        <>
                            <PageInfo
                                count={candidates.length}
                                total={pagination.totalDocuments}
                                currentPage={pagination.currentPage}
                                totalPages={pagination.totalPages}
                            />
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={!pagination.hasPreviousPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-double-left"></i>
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={!pagination.hasPreviousPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-left"></i>
                                </button>
                                <span className="pagination-page-info">
                                    {t('common.page')} {pagination.currentPage} {t('common.of')} {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={!pagination.hasNextPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-right"></i>
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={!pagination.hasNextPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-double-right"></i>
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}

        </div>
    );
};

export default ReportSourceCandidatesView;
